import { SuccessResponseList } from './common'
import { ResponseDatasetVersion } from './datasetVersion'
import { User } from './user'
import { ScopeAssignment } from './user-management'

export enum DeliverableSetStatus {
  DRAFT = 'draft',
  IN_REVIEW = 'in_review',
  APPROVED = 'approved',
  ARCHIVED = 'archived',
}

export enum DeliverableSetSyncStatus {
  NOT_SYNCED = 'not_synced',
  SYNCED = 'synced',
  SYNCING = 'syncing',
  FAILED = 'failed',
  DELETED = 'deleted',
}

/**
 * DTO for creating a new deliverable set.

 A deliverable set represents a collection of clinical trial deliverables
 associated with a specific project and reporting event.
 */
export interface RequestCreateDeliverableSet {
  /** Optional user ID who is creating this deliverable set */
  created_by?: string | null
  /** Optional list of dataset version IDs to associate with this deliverable set */
  dataset_version_ids?: string[] | null
  /**
   * Deliverable set name
   * @minLength 1
   * @maxLength 255
   */
  deliverable_set_name: string
  /** Optional user ID of the deliverable set owner_id */
  owner_id?: string | null
  /** ID of the project this deliverable set belongs to */
  project_id: string
  /** ID of the reporting event this deliverable set is for */
  reporting_event_id: string
  /** Optional date when the review period opens */
  review_open_date?: string | null
  /** Initial status of the deliverable set (draft, in_review, approved, archived) */
  status: DeliverableSetStatus
  /** Optional Teamwork resource ID (tasklist or task) for integration tracking */
  teamwork_resource_id?: string | null
  /** Optional Teamwork resource type ('tasklist' or 'task') */
  teamwork_resource_type?: string | null
  /** Optional status of Teamwork synchronization */
  teamwork_sync_status?: string | null
  /** Optional URL to the Teamwork resource for synchronization tracking */
  teamwork_sync_url?: string | null
  /** Optional timestamp when Teamwork sync occurred */
  teamwork_synced_at?: string | null
}

/**
 * DTO for updating an existing deliverable set.

 All fields are optional - only provided fields will be updated.
 Use this for partial updates (PATCH operations).
 */
export interface RequestUpdateDeliverableSet {
  /** Updated list of dataset version IDs to associate with this deliverable set */
  dataset_version_ids?: string[] | null
  /** Updated name of the deliverable set */
  deliverable_set_name?: string | null
  /** Updated owner user ID */
  owner_id?: string | null
  /** ID of the project this deliverable set belongs to */
  project_id?: string | null
  /** ID of the reporting event this deliverable set is for */
  reporting_event_id?: string | null
  /** Updated review open date */
  review_open_date?: string | null
  /** Updated status (draft, in_review, approved, archived) */
  status?: DeliverableSetStatus | null
  /** Updated Teamwork resource ID (tasklist or task) for integration tracking */
  teamwork_resource_id?: string | null
  /** Updated Teamwork resource type ('tasklist' or 'task') */
  teamwork_resource_type?: string | null
  /** Updated status of Teamwork synchronization */
  teamwork_sync_status?: string | null
  /** Updated URL to the Teamwork resource for synchronization tracking */
  teamwork_sync_url?: string | null
  /** Updated timestamp when Teamwork sync occurred */
  teamwork_synced_at?: string | null
}

export interface RequestApprovalDeliverableSet {
  /** Due date of deliverable set */
  due_date: string
  /** Optional note when submitting approval request */
  note?: string | null
  /** Owner user ID */
  owner_id?: string
  /** URL to redirect the user after approval process is completed */
  redirect_url: string
  /** List of reviewer user IDs */
  reviewers: RequestApprovalUserInvitation[] | null
}

export interface RequestCancelApprovalRequest {
  note?: string | null
}

export interface RequestDelegateReview {
  current_reviewer_id: string
  new_reviewer_id: string
  deliverable_set_id: string
  note?: string
}

/**
 * Deliverable set response with enriched data from related entities.

 Includes company name, project name, and reporting event name
 fetched via database joins for display purposes.
 */
export interface ResponseDeliverableSet {
  /** Associated company ID */
  company_id?: string
  /** Name of the associated company (fetched via join) */
  company_name?: string
  /** Creation timestamp (UTC) */
  created_at?: string
  /** User ID who created this deliverable set */
  created_by?: string | null
  /** User details for created_by audit field */
  created_by_user?: User | null
  /** List of dataset version IDs associated with this deliverable set */
  dataset_version_ids?: string[] | null
  /** Optional detailed dataset version info associated with this deliverable set */
  dataset_versions?: ResponseDatasetVersion[] | null
  /** Soft delete timestamp (UTC) */
  deleted_at?: string | null
  /** User ID who deleted this deliverable set */
  deleted_by?: string | null
  /** User details for deleted_by audit field */
  deleted_by_user?: User | null
  /** Name of the deliverable set */
  deliverable_set_name: string
  /** Short name (used in nested responses like comments) */
  name?: string
  /** Draft approval request data stored as JSON */
  draft_approval?: RequestDraftApprovalDeliverableSet | null
  /** Deadline for deliverable completion */
  due_date?: string | null
  /** Unique identifier for the deliverable set */
  id: string
  /** Timestamp of the most recent activity */
  last_activity_date?: string | null
  /** Note associated with the last approval request */
  last_approval_note?: string | null
  /** Timestamp of the last approval request */
  last_approval_request_date?: string | null
  /** User details for owner */
  owner?: User | null
  /** User ID of the deliverable set owner_id */
  owner_id?: string | null
  /** Associated project ID */
  project_id: string
  /** Name of the associated project (fetched via join) */
  project_name: string
  /** Associated reporting event ID */
  reporting_event_id: string
  /** Name of the reporting event (fetched via join) */
  reporting_event_name: string
  /** Date when the review period opens */
  review_open_date?: string | null
  /** List of reviewers assigned to this deliverable set with user details */
  reviewers?: User[] | null
  /** Number of open comments in this deliverable set */
  snapshot_comment_open?: number | null
  /** Number of resolved comments in this deliverable set */
  snapshot_comment_resolved?: number | null
  /** Total number of comments in this deliverable set */
  snapshot_comment_total?: number | null
  /** Number of approved TFLs in this deliverable set */
  snapshot_tfl_approved?: number | null
  /** Number of TFLs with open comments in this deliverable set */
  snapshot_tfl_has_open_comment?: number | null
  /** Number of TFLs in review in this deliverable set */
  snapshot_tfl_in_review?: number | null
  /** Number of new TFLs in this deliverable set */
  snapshot_tfl_new?: number | null
  /** Progress percentage of approved TFLs (snapshot_tfl_approved / snapshot_tfl_total * 100), 0.0 if no TLFs */
  snapshot_tfl_progress: number
  /** Number of TFLs with query raised in this deliverable set */
  snapshot_tfl_query_raised?: number | null
  /** Total number of TFLs in this deliverable set */
  snapshot_tfl_total?: number | null
  /** Number of TFLs under revision in this deliverable set */
  snapshot_tfl_under_revision?: number | null
  /** Current status (draft, in_review, approved, archived) */
  status: DeliverableSetStatus
  /** Teamwork resource ID (tasklist or task) linked to this deliverable set */
  teamwork_resource_id?: string | null
  /** Teamwork resource type ('tasklist' or 'task') */
  teamwork_resource_type?: string | null
  /** Status of the last Teamwork synchronization (success, error, etc.) */
  teamwork_sync_status?: DeliverableSetSyncStatus | null
  /** URL to the Teamwork resource for synchronization tracking */
  teamwork_sync_url?: string | null
  /** Timestamp of the last Teamwork synchronization */
  teamwork_synced_at?: string | null
  /** Last update timestamp (UTC) */
  updated_at: string
  /** User ID who last updated this deliverable set */
  updated_by?: string | null
  /** User details for updated_by audit field */
  updated_by_user?: User | null
  /** List of viewers assigned to this deliverable set with user details */
  viewers?: User[] | null

  // Sorting only (virtual field for filtering/sorting)
  project_reporting_name?: string
}

export interface SuccessResponseListResponseDeliverableSet extends SuccessResponseList<ResponseDeliverableSet> {}

/**
 * DTO for system user information in deliverable set context.
 */
export interface RequestApprovalUserInvitation {
  /** Email address of the system user */
  email: string
  /** First name of the system user */
  first_name: string
  /** Unique identifier of the system user */
  id?: string
  /** Last name of the system user */
  last_name: string
  /** Optional role ID to assign to the user */
  role_id?: string | null
  /** Role name for this user (reviewer or viewer) */
  role_name?: string | null
  /** Optional list of resource scopes for the user */
  scopes?: ScopeAssignment[]
}

/**
 * DTO for saving draft approval requests - redirect_url is optional.
 */
export interface RequestDraftApprovalDeliverableSet {
  /** Due date of deliverable set */
  due_date: string
  /** Optional note when submitting approval request */
  note?: string | null
  /** Owner user ID */
  owner_id: string
  /** Optional URL to redirect the user after approval process is completed */
  redirect_url?: string | null
  /** List of reviewer user IDs */
  reviewers: RequestApprovalUserInvitation[] | null
}
